import { defineStore } from 'pinia'
import type { AdPlacement } from '../../ad_ops/adPlacementMappings'
import debug from '../../utils/console'

const logger = debug.withTag('AdPlacementConfigStore')

interface AppConfigAdOps {
  placements: Record<string, Record<string, AdPlacement>>
}

interface AppConfig {
  adops: AppConfigAdOps
}

/**
 * Pinia store for managing ad placement configurations
 */
export const adConfigStore = defineStore('adPlacementConfig', {
  state: (): {
    configs: Record<string, Record<string, AdPlacement>>
    isInitialized: boolean
  } => ({
    configs: {},
    isInitialized: false,
  }),

  actions: {
    initialize() {
      if (this.isInitialized) {
        return
      }

      const config = useAppConfig()
      const isAppConfig = (obj: unknown): obj is AppConfig => {
        return obj !== null && typeof obj === 'object' && 'adops' in obj
      }

      if (isAppConfig(config)) {
        this.configs = config.adops?.placements || {}
      } else {
        this.configs = {}
      }

      this.isInitialized = true
    },

    isValidAdType(
      key: string,
      placementConfigs: Record<string, AdPlacement>
    ): key is keyof AdPlacement {
      return key in (placementConfigs || {})
    },

    getAdTypeConfigs(
      adProvider: string,
      placementConfigs: Record<string, AdPlacement>
    ) {
      // Always return base config with style and props, even if provider is missing
      const baseConfig = {
        ...placementConfigs?.style,
        ...placementConfigs?.props,
      }

      // If provider exists, merge it with base config
      if (this.isValidAdType(adProvider, placementConfigs)) {
        return {
          ...placementConfigs[adProvider],
          ...baseConfig,
        }
      }

      // Return base config even if provider is missing
      return baseConfig
    },

    getConfigForPlacement(placement: string, provider: string) {
      this.initialize()
      const placementConfigs = this.configs[placement]

      if (!placementConfigs) {
        logger.error(`Missing ad placement configs for "${placement}"`)
        return null
      }

      const config = this.getAdTypeConfigs(provider, placementConfigs)
      const hasProviderConfig = this.isValidAdType(provider, placementConfigs)

      if (!hasProviderConfig) {
        // DXT-1780: Log missing provider but still return base config
        logger.warn(
          `Missing provider "${provider}" for placement "${placement}", returning base config with style/props`,
          {
            placement,
            provider,
            availableProviders: Object.keys(placementConfigs),
            returnedConfigKeys: Object.keys(config),
            timestamp: Date.now(),
          }
        )
      }

      return config
    },
  },
})
